// Thin wrapper component that owns the usePullToRefresh hook and
// renders the proportional 360° indicator above its content. Callers
// pass a children block + onRefresh callback + optional indicator
// positioning props.
//
// Why a wrapper component rather than calling the hook in App.js:
// App.js is a giant single-component file with conditional return
// statements per view. Calling usePullToRefresh inside an
// `if (view === 'journal')` branch violates Rules of Hooks. Wrapping
// the conditionally-rendered subtree in a component scopes the hook
// to that component's lifecycle, sidestepping the issue.
//
// The indicator is positioned absolutely at the top-center of the
// container, with vertical translate driven by `pullDistance` so
// it slides down with the pull. When `isRefreshing`, it sits at
// the threshold position and spins.

import React from 'react';
import usePullToRefresh, { PullToRefreshIndicator } from './usePullToRefresh';

export default function PullToRefreshContainer({
  onRefresh,
  children,
  style,
  indicatorTop = 4,
  threshold = 70,
  ...rest
}) {
  const { pullDistance, isRefreshing, progress, reduceMotion, bind } = usePullToRefresh(onRefresh, { threshold });

  // The indicator sits at indicatorTop and slides down with the pull
  // (capped at threshold) so it tracks the user's finger without
  // running off the screen.
  const indicatorY = Math.min(pullDistance, threshold);

  return (
    <div
      {...rest}
      {...bind}
      style={{
        ...(style || {}),
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: `${indicatorTop}px`,
          left: '50%',
          transform: `translate(-50%, ${indicatorY}px)`,
          pointerEvents: 'none',
          zIndex: 50,
          transition: reduceMotion ? 'none' : (pullDistance === 0 ? 'transform 180ms ease-out' : 'none'),
        }}
      >
        <PullToRefreshIndicator
          progress={progress}
          isRefreshing={isRefreshing}
          reduceMotion={reduceMotion}
          size={28}
        />
      </div>
      {children}
    </div>
  );
}
